import { Empty, List, notification } from "antd";
import { getRequest } from "api/apiClient";
import { OpButton } from "components/customAntd/DLS/OpButton/OpButton";
import { useSelector } from "react-redux";
import { RootState } from "store/store";
import { Agreement } from "types/agreementTypes";
import { DocumentCheck } from "types/visitTypes";
import { CheckOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

interface AgreementsTabProps {
    extendedVisitorInfo: DocumentCheck;
    attendantAgreements: Agreement[];
    kioskAgreements: Agreement[];
    signedAgreements: Agreement[];
}

const AgreementsTab: React.FC<AgreementsTabProps> = ({ extendedVisitorInfo, attendantAgreements, kioskAgreements, signedAgreements }) => {
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const { selectedVisit, selectedVisitor } = useSelector((state: RootState) => state.visits);
    const { selectedSignInWorkflowConfig } = useSelector((state: RootState) => state.visitorWorkflows);

    const { t } = useTranslation();
    const handleViewSignedAgreements = async () => {
        try {
            const response = await getRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/visitors/${selectedVisitor?.id}/agreement`, {}, {
                responseType: 'blob'
            });
            const blob = new Blob([response], { type: 'application/pdf' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'agreements.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error: any) {
            if (error.response?.status === 404) {
                notification.warning({
                    message: t('visitors.profile.notification.agreementNotFoundWarning'),
                    description: t('visitors.profile.notification.agreementNotFoundWarningDescription'),
                    placement: 'bottomRight',
                });
                return;
            }
            console.error('Failed to fetch visitor agreements.', error);
        }
    };
    const isDisplayEnabled = selectedSignInWorkflowConfig?.agreementsDisplay === 1;
    const hasSignedPDF = !!extendedVisitorInfo?.agreement;

    let displayAgreements: Agreement[] = [];

    if (isDisplayEnabled) {
        displayAgreements = [
            ...attendantAgreements,
            ...signedAgreements,
        ];

        if (hasSignedPDF) {
            displayAgreements = [
                ...attendantAgreements,
                ...signedAgreements,
                ...kioskAgreements,
            ];
        }
    } else if (!isDisplayEnabled) {
        displayAgreements = [
            ...signedAgreements,
        ];

        if (hasSignedPDF) {
            displayAgreements = [
                ...signedAgreements,
                ...kioskAgreements,
            ];
        }
    }

    //Remove duplicates based on id
    displayAgreements = Array.from(new Map(displayAgreements.map(a => [a.id, a])).values());

    return (
        <>
            <List
                itemLayout="horizontal"
                dataSource={displayAgreements}
                bordered
                locale={{
                    emptyText: <Empty description={t('visitors.profile.agreements.noData')} />
                }}
                renderItem={(agreement) => (
                    <List.Item
                        extra={
                            signedAgreements.some(signedAgreement =>
                                signedAgreement.id === agreement.id
                            ) && <CheckOutlined style={{ color: 'green' }} />
                        }
                    >
                        <List.Item.Meta
                            title={
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    {agreement.name}
                                </div>
                            }
                        />
                    </List.Item>
                )}
            />
            {(extendedVisitorInfo?.agreement != null) && (
                <OpButton onClick={handleViewSignedAgreements} type="primary" ghost style={{ width: '100%', marginTop: '10px' }}>
                    {t('visitors.profile.agreements.viewSigned')}
                </OpButton>
            )}
        </>
    );
};

export default AgreementsTab;
